import type {
  MemoAction,
  MemoActionConflict,
  MemoKeyPoint,
  PreviousAnalysis,
} from '@ai-meeting/shared'

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string')
}

export function normalizeActions(value: unknown): MemoAction[] {
  if (!Array.isArray(value)) return []

  return value
    // Annotated rather than inferred: `conflict` is optional AND nullable on `MemoAction`, and an
    // inferred literal that only ever omits it is not the same type, which breaks the predicate
    // below rather than the object above.
    .map((item): MemoAction | null => {
      if (typeof item === 'string') {
        const content = item.trim()
        return content ? { content, priority: null } : null
      }

      if (!item || typeof item !== 'object') return null

      const record = item as Record<string, unknown>
      const content = String(record.content ?? record.text ?? record.action ?? '').trim()
      if (!content) return null

      // `raised_by` is the AI service's spelling; `raisedBy` covers anything that has been through
      // a camel-casing layer on the way here. Absent stays absent rather than becoming '' so the
      // renderer's "no name" branch is the same for a legacy memo and a genuinely ownerless item.
      const raisedBy = String(record.raised_by ?? record.raisedBy ?? '').trim()
      const conflict = normalizeConflict(record.conflict)

      return {
        content,
        priority: typeof record.priority === 'string' ? record.priority.trim() : null,
        ...(raisedBy ? { raised_by: raisedBy } : {}),
        ...(conflict ? { conflict } : {}),
      }
    })
    .filter((item): item is MemoAction => item !== null)
}

/** Whether this string is a URL safe to put in an href. */
function isHttpUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value)
    return protocol === 'http:' || protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Validate one action's `conflict` block (BRAI-549), or drop it.
 *
 * `link` ends up in an href, so its scheme is checked rather than trusted. The value reaches us
 * from a service that is not ours, and `javascript:` in an href runs in this origin — the check
 * costs nothing and the alternative is a live meeting screen as the place to find that out.
 *
 * A block that fails any check is dropped whole, not field by field. The item then renders as the
 * ordinary confirm-item it also is: `content` already states both values, so the reader loses the
 * comparison table and nothing else. A half-built card — an issue key with no link, or a link with
 * no key — would invite the reader to go and check something they cannot reach.
 */
function normalizeConflict(value: unknown): MemoActionConflict | null {
  if (!value || typeof value !== 'object') return null

  const record = value as Record<string, unknown>
  const field = record.field
  if (field !== 'deadline' && field !== 'status') return null

  const issueKey = typeof record.issue_key === 'string' ? record.issue_key.trim() : ''
  if (!issueKey) return null

  const link = typeof record.link === 'string' ? record.link.trim() : ''
  if (!isHttpUrl(link)) return null

  // `current` and `incoming` are nullable by contract: one side of the disagreement can genuinely
  // be "nothing set yet", which is different from the field being absent and must still render.
  return {
    issue_key: issueKey,
    field,
    current: typeof record.current === 'string' ? record.current.trim() || null : null,
    incoming: typeof record.incoming === 'string' ? record.incoming.trim() || null : null,
    link,
  }
}

/**
 * One Confirm Action line: `[priority] Speaker: content`.
 *
 * The speaker prefix is the whole point of the field — a list of "Xác nhận muốn cải thiện tiếng
 * Nhật" repeated per participant is unanswerable without knowing whose goal each line is. No name
 * (ownerless item, or a memo from before the field existed) renders exactly as it always did.
 *
 * The already-prefixed check is not paranoia: the AI service is told to keep the name OUT of
 * `content` precisely because this function adds it, and an older analysis carried forward inside a
 * meeting may still have it inline — printing "Tu: Tu muốn…" would be the visible regression.
 */
export function actionToText(action: MemoAction): string {
  const speaker = action.raised_by?.trim() ?? ''
  const body =
    speaker && !action.content.toLowerCase().startsWith(`${speaker.toLowerCase()}:`)
      ? `${speaker}: ${action.content}`
      : action.content
  return action.priority ? `[${action.priority}] ${body}` : body
}

function normalizeKeyPoint(item: unknown): MemoKeyPoint | null {
  if (typeof item === 'string') {
    const topic = item.trim()
    return topic ? { topic, content: '' } : null
  }

  if (!item || typeof item !== 'object') return null

  const record = item as Record<string, unknown>
  const topic = typeof record.topic === 'string' ? record.topic.trim() : ''
  const content = typeof record.content === 'string' ? record.content.trim() : ''

  if (!topic && !content) return null
  return {
    topic: topic || content,
    content,
  }
}

export function normalizeKeyPoints(value: unknown): MemoKeyPoint[] {
  if (!Array.isArray(value)) return []

  return value
    .map((item) => normalizeKeyPoint(item))
    .filter((item): item is MemoKeyPoint => item !== null)
}

export function normalizeMemoResponse(data: unknown): PreviousAnalysis {
  const root = (data ?? {}) as Record<string, unknown>
  const nested =
    root.previous_analysis && typeof root.previous_analysis === 'object'
      ? (root.previous_analysis as Record<string, unknown>)
      : root

  return {
    main_content: asStringArray(nested.main_content),
    key_points: normalizeKeyPoints(nested.key_points),
    summary: typeof nested.summary === 'string' ? nested.summary : '',
    actions: normalizeActions(nested.actions),
  }
}
